/**
 * PendingActionBanner - Shows pending confirmation requests
 * 
 * ARC Doctrine: Calm language, no urgency, no blame
 */
import { useState, useEffect, useCallback } from "react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api from "../../lib/api";
import { 
  Clock, 
  CheckCircle2, 
  X, 
  AlertCircle,
  RefreshCw,
  Shield,
  Settings,
  Trash2,
  Archive
} from "lucide-react";

// Action type display info
const ACTION_TYPE_INFO = {
  change_privacy_style: {
    icon: Shield,
    label: "Privacy settings change",
    description: (payload) => `Change privacy style to "${payload.style}"`
  },
  delete_account: {
    icon: Trash2,
    label: "Account deletion",
    description: (payload) => `Delete account`
  },
  archive_account: {
    icon: Archive,
    label: "Account archive",
    description: (payload) => `Archive account`
  },
  change_cadence: {
    icon: Clock,
    label: "Review cadence change",
    description: (payload) => `Change cadence to "${payload.cadence_type}"`
  },
  change_budget_structure: {
    icon: Settings,
    label: "Budget structure change",
    description: (payload) => `Change structure to "${payload.structure_type}"`
  }
};

export function PendingActionBanner({ 
  householdId,
  isCreator = false,
  onActionResolved,
  className = ""
}) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchActions = useCallback(async () => {
    try {
      const res = await api.get("/api/household/pending-actions");
      if (res.ok) {
        const data = await res.json();
        setActions(data.actions || []);
      }
    } catch (err) {
      console.error("Failed to fetch pending actions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const handleApprove = async (actionId) => {
    setActionLoading(actionId);
    try {
      const res = await api.post(`/api/household/action/${actionId}/approve`, {});
      if (res.ok) {
        setActions(prev => prev.filter(a => a.action_id !== actionId));
        onActionResolved?.("approved", actionId);
      }
    } catch (err) {
      console.error("Failed to approve action:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (actionId) => {
    setActionLoading(actionId);
    try {
      const res = await api.post(`/api/household/action/${actionId}/decline`, {});
      if (res.ok) {
        setActions(prev => prev.filter(a => a.action_id !== actionId));
        onActionResolved?.("declined", actionId);
      }
    } catch (err) {
      console.error("Failed to decline action:", err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (actionId) => {
    setActionLoading(actionId);
    try {
      const res = await api.delete(`/api/household/action/${actionId}`);
      if (res.ok) {
        setActions(prev => prev.filter(a => a.action_id !== actionId));
        onActionResolved?.("cancelled", actionId);
      }
    } catch (err) {
      console.error("Failed to cancel action:", err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading || actions.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid="pending-action-banner">
      {actions.map((action) => {
        const typeInfo = ACTION_TYPE_INFO[action.action_type] || {
          icon: AlertCircle,
          label: action.action_type,
          description: () => "Pending change"
        };
        const Icon = typeInfo.icon;
        const isMyAction = !isCreator; // If not creator, it's my action to cancel
        const canApproveDecline = isCreator;
        const isLoading = actionLoading === action.action_id;

        return (
          <Alert 
            key={action.action_id} 
            className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800"
            data-testid={`pending-action-${action.action_id}`}
          >
            <Icon className="w-4 h-4 text-amber-600" />
            <div className="flex-1">
              <AlertTitle className="text-amber-900 dark:text-amber-200 flex items-center gap-2">
                {typeInfo.label}
                <Badge 
                  variant="outline" 
                  className="border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  Pending confirmation
                </Badge>
              </AlertTitle>
              <AlertDescription className="text-amber-800 dark:text-amber-300 mt-1">
                <span className="block">
                  {typeInfo.description(action.payload)}
                </span>
                {action.reason && (
                  <span className="block mt-1 text-sm opacity-80">
                    Reason: {action.reason}
                  </span>
                )}
                <span className="block mt-1 text-sm opacity-80">
                  Requested by {action.initiated_by_name || "Partner"}
                </span>
              </AlertDescription>
              
              <div className="flex gap-2 mt-3">
                {canApproveDecline ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleApprove(action.action_id)}
                      disabled={isLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`approve-action-${action.action_id}`}
                    >
                      {isLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                      )}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDecline(action.action_id)}
                      disabled={isLoading}
                      className="border-amber-400 text-amber-700 hover:bg-amber-100"
                      data-testid={`decline-action-${action.action_id}`}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Decline
                    </Button>
                  </>
                ) : isMyAction ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCancel(action.action_id)}
                    disabled={isLoading}
                    className="text-muted-foreground"
                    data-testid={`cancel-action-${action.action_id}`}
                  >
                    {isLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4 mr-1" />
                    )}
                    Cancel request
                  </Button>
                ) : null}
              </div>
            </div>
          </Alert>
        );
      })}
    </div>
  );
}

export default PendingActionBanner;
